<script setup lang="ts">
import { computed, ref, toRef, type CSSProperties } from 'vue';
import { useOnScreen } from './holo/useOnScreen';
import { useMatte } from './holo/useMatte';
import type { MatteMode } from './holo/matte';
import { initialsFromName } from './holo/initials';
import { fallbackOnError } from '@/utils/imageFallback';
import { avatarPlaceholderUrl } from '@/utils/avatarPlaceholder';
import defaultPedestal from '@/assets/pedestal.webp';
import FlowSignMark from './FlowSignMark.vue';

export type HoloQuality = 'low' | 'medium' | 'high';
export type HoloState = 'normal' | 'dimmed' | 'unstable' | 'offline';

interface Props {
  /** Any image — an upload, a blob/data URL, a remote link. Backdrop removal
   *  and matte extraction happen in the browser, once per URL. */
  art?: string;
  /** Character name. Only used to derive the initials shown when `art` is empty. */
  name?: string;
  /** Pedestal art, shared by every token on the board. */
  pedestal?: string;
  /** Projection colour — anything CSS accepts. Drives tint, glow and label. */
  color?: string;
  /** Rendered width in px. Height follows the 1:1.55 token ratio. */
  size?: number;
  /** Override the backdrop-removal strategy. `auto` inspects the border pixels
   *  and picks; `cutout` for a studio backdrop, `luminance` for art on black,
   *  `raw` to skip the pixel pass entirely. */
  matte?: MatteMode;
  quality?: HoloQuality;
  state?: HoloState;
  /** Global multiplier on every emissive layer. 0–1.5 is the useful range. */
  intensity?: number;
  /** Desynchronises the float/ring/mote cycles. Pass a stable per-token number. */
  seed?: number;
  /** Seconds for one float cycle. */
  speed?: number;
  /**
   * Rotation of the ground plane the pedestal sits on, in degrees.
   *
   * The projection is a billboard and stays upright, but the pedestal is an
   * object lying on the floor: on a map whose grid is raked, leaving it
   * axis-aligned makes it read as a sticker on the screen rather than a thing
   * standing in a cell. Pass the on-screen angle of the cell's own x axis.
   */
  groundTilt?: number;
  /**
   * The one status ring. It is drawn on the pedestal's own ground ellipse and
   * carried by the same transform, so it can never drift from the base the way
   * a ring drawn onto the map's grid does — the pedestal artwork and the map are
   * rendered from different camera heights and their ground planes do not agree.
   *
   * `own` marks the viewer's character and holds steady; `selected` pulses;
   * `targeted` means an armed ability currently resolves against this figure and
   * outranks the rest - it is the one marker the player must not miss.
   */
  marker?: 'none' | 'own' | 'hover' | 'selected' | 'targeted';
  /**
   * The fight side this figure belongs to, as a CSS colour — a
   * `sideColorVar(slot)` value from `utils/sideColor.js`. A second, independent
   * ring from `marker`: allegiance and hostility are two different facts, and
   * `color` above already carries hostility (self/ally/enemy/neutral). Undefined
   * or empty draws no ring at all — a sideless character, or one outside a fight.
   *
   * Only drawn here when `plate === 'sprite'`, the same gate the status ring
   * already uses: under a real 3D pedestal (`plate === 'none'`) the map draws
   * this ring itself, on the ground plane, because the artwork's own contact
   * ellipse these CSS vars are measured against belongs to a camera the 3D prop
   * does not share.
   */
  sideColor?: string;
  /**
   * The side's own mark — an SVG path `d` on a 24-unit square, from
   * `utils/sideEmblems.js`. Drawn upright at the front of the side ring, under
   * the same gate as the ring itself.
   *
   * The ring's colour alone answers "which side" only for a viewer who has
   * learnt the palette, and it answers it badly on a lit board where a thin
   * coloured hairline is the first thing the scene washes out. The emblem is
   * the sign the side was introduced by on the establishment screen and wears
   * on the rail, so a token can be read against either without a key.
   */
  sideEmblem?: string;
  /**
   * What the figure stands on.
   *
   * `sprite` draws the pedestal artwork, which is right anywhere the token is a
   * self-contained thing — a card, a roster, a preview. `none` leaves the plate to
   * whoever owns the scene: the tactical map now stands a real 3D prop in the cell,
   * and a sprite drawn from its own baked camera on top of that would be two
   * pedestals disagreeing about where the floor is.
   *
   * The measurements all stay: the figure's height, its beam and its emitter are
   * still expressed against the plate, so the figure stands in the same place either
   * way. What goes with the artwork is the status ring, which was drawn on the
   * artwork's own contact ellipse and has nowhere to be without it — under a prop,
   * the prop's trim carries the status instead.
   */
  plate?: 'sprite' | 'none';
  /**
   * Where the projection is emitted from, as a fraction of the token's height
   * measured up from its bottom edge.
   *
   * The default is measured off the pedestal ARTWORK — the lens in the sprite. Once
   * the plate is a prop in the scene that lens does not exist, and a cone struck
   * from where it used to be starts below and wider than the object the light is
   * supposed to be coming out of. The scene knows where the real plate's top face
   * is; it passes that here, and the beam, the cone the figure is carved by, and the
   * emitter dot all move together because all three are derived from this one point.
   */
  emitY?: number;
  /**
   * What the emitter projects.
   *
   * `card` throws the artwork up as a panel — a trapezoid standing on the beam, wide
   * at the top, tinted rather than recoloured, and translucent. It touches no pixels:
   * the picture goes up exactly as it was painted.
   *
   * `figure` is the volumetric reading — the subject cut out of its backdrop and
   * projected as a body standing in the cell. It looks better when it works, and
   * whether it works is a property of the artwork rather than of this component. It
   * has to decide what "the subject" is from an arbitrary picture, and every answer
   * is a guess: a dark robe reads as backdrop, flowing hair reads as filigree, a
   * full-bleed illustration has no separable subject at all. Art that guesses wrong
   * does not degrade, it fails — a lace silhouette or a coloured blob.
   *
   * The card has no such failure mode, because it has no such question. That is why
   * it is the default: a slightly plainer thing that is right for every picture beats
   * a richer one that is right for some of them.
   *
   * `plate` is the emitter with nothing coming out of it — no projection, no beam.
   * It is what a destination is: the move preview marks a place on the floor, and
   * standing a second copy of the character on it says that they are somehow already
   * there. An empty plate says the thing that is actually true, which is that this is
   * where they would arrive.
   */
  variant?: 'card' | 'figure' | 'plate';
  selected?: boolean;
  /** Freeze animations regardless of visibility. Tokens are already paused
   *  automatically while off screen. */
  paused?: boolean;
  /** Character is Signed with Flow — under game master authority. */
  signed?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  art: undefined,
  name: undefined,
  pedestal: undefined,
  color: '#22e3ff',
  size: 180,
  matte: 'auto',
  quality: 'high',
  state: 'normal',
  intensity: 1,
  seed: 0,
  speed: 6,
  groundTilt: 0,
  marker: 'none',
  sideColor: undefined,
  sideEmblem: undefined,
  plate: 'sprite',
  emitY: undefined,
  variant: 'card',
  selected: false,
  paused: false,
  signed: false,
});

const root = ref<HTMLElement | null>(null);
const onScreen = useOnScreen(root);

/*
 * The extractor is not merely unused by the card, it is not RUN for it. Matte
 * extraction is a full pixel pass — a flood fill, a Sobel, two morphological passes —
 * and leaving it wired up to feed a layer nobody draws would spend all of that per
 * distinct avatar to produce a bitmap that is discarded.
 */
const matteSrc = computed(() => (props.variant === 'figure' ? props.art : undefined));
const projection = useMatte(matteSrc, toRef(props, 'matte'));

const pedestalUrl = computed(() => props.pedestal || defaultPedestal);

const initials = computed(() => initialsFromName(props.name));

const active = computed(() => onScreen.value && !props.paused);

/**
 * Marker gain. This has to be folded into the inline value rather than set from a
 * stylesheet rule: --ht-intensity is written inline from the prop, and inline
 * custom properties beat any selector, so a [data-marker] rule for it never lands.
 */
const MARKER_GAIN = { none: 1, own: 1.06, hover: 1.28, selected: 1.45, targeted: 1.6 } as const;

/** Length of the long cycle, in seconds. Mirrors --ht-idle in the stylesheet. */
const IDLE_SECONDS = 54;

/** Deterministic 0–1 from an integer seed; keeps a board from breathing in lockstep. */
function jitter(seed: number): number {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

/**
 * Below this rendered width the token is a piece on a board, not a picture.
 *
 * The distinction is not cosmetic. Every atmospheric part of this component — the
 * dissolve into the beam, the cone that carves the shoulders, the elliptical
 * falloff — is expressed as a FRACTION of the token, so it costs the same share of
 * the figure at every size. At 340px, spending 38% of the figure on a dissolve
 * still leaves a face two hundred pixels tall. At 114px it leaves sixty-five, and
 * sixty-five pixels of person under a wash of gradients is the blob this component
 * was accused of being.
 *
 * So the board tier is not a lower-quality tier; it is a different BUDGET. The
 * character takes the space back and the atmosphere gives it up, because at that
 * size the character is the only thing that can still be read.
 */
const BOARD_MAX_PX = 130;

const scaleTier = computed(() => (props.size < BOARD_MAX_PX ? 'board' : 'card'));

const flowSignSize = computed(() => Math.round(props.size * (scaleTier.value === 'board' ? 0.24 : 0.16)));

const vars = computed<CSSProperties>(() => {
  const r = jitter(props.seed);
  return {
    '--ht-size': `${props.size}px`,
    '--ht-color': props.color,
    '--ht-intensity': props.intensity * MARKER_GAIN[props.marker],
    '--ht-art': `url("${projection.value.url}")`,
    '--ht-rim': projection.value.rimUrl ? `url("${projection.value.rimUrl}")` : 'none',
    '--ht-pedestal': `url("${pedestalUrl.value}")`,
    '--ht-art-mode': projection.value.maskMode,
    '--ht-dur': `${props.speed * (0.85 + r * 0.3)}s`,
    '--ht-phase': `${-r * props.speed * 2}s`,
    /*
     * A second, independent offset for the long cycle.
     *
     * --ht-phase spans two float periods - about fourteen seconds - which spreads
     * the drift and the beam fine, but against a fifty-four second idle cycle it
     * would bunch every token's stutter into the same quarter of it and the board
     * would visibly ripple. This spans the whole cycle, from a different corner of
     * the same seed so the two never line up.
     */
    '--ht-idle-phase': `${-jitter(props.seed + 977) * IDLE_SECONDS}s`,
    '--ht-ground-tilt': `${props.groundTilt}deg`,
    ...(props.emitY === undefined ? {} : { '--ht-emit-y': props.emitY }),
    ...(props.sideColor ? { '--ht-side-color': props.sideColor } : {}),
  } as CSSProperties;
});

/** Drawn only on the sprite plate — see the `sideColor` prop doc for why. */
const showSideRing = computed(() => props.plate === 'sprite' && Boolean(props.sideColor));

/* The mark rides the ring: no ring, nothing for it to sit on. */
const showSideMark = computed(() => showSideRing.value && Boolean(props.sideEmblem));

/*
 * A portrait whose row survives its file — a media path the server still
 * records and no longer serves — loads nothing and leaves the panel an empty
 * box, because a broken <img> has no intrinsic size to fall back to and the
 * initials fallback only covers the case where `art` was never given.
 *
 * The token knows nothing about the character beyond a picture and a name, and
 * gender is not part of that (nor should it become a prop chain threaded
 * through every board), so this is `other.png` — the same default
 * `CharacterBiography.avatar` carries on the server.
 */
const artPlaceholder = avatarPlaceholderUrl();

const onArtError = (event: Event) => fallbackOnError(event, artPlaceholder);
</script>

<template>
  <div
    ref="root"
    class="holotok"
    :style="vars"
    :data-quality="quality"
    :data-state="state"
    :data-selected="String(selected)"
    :data-marker="marker"
    :data-plate="plate"
    :data-active="String(active)"
    :data-ready="String(projection.ready)"
    :data-matte="projection.mode"
    :data-scale="scaleTier"
    :data-rim="String(Boolean(projection.rimUrl))"
    :data-variant="variant"
  >
    <!-- The panel. The artwork is placed, not processed. -->
    <div v-if="variant === 'card'" class="holotok__card">
      <img
        v-if="art"
        class="holotok__cardArt"
        :src="art"
        alt=""
        draggable="false"
        loading="lazy"
        decoding="async"
        @error="onArtError"
      />
      <div v-else class="holotok__cardFallback">{{ initials }}</div>
      <div class="holotok__cardTint"></div>
      <div class="holotok__cardLines"></div>
      <div class="holotok__sweep"></div>
    </div>

    <div v-else-if="variant === 'figure'" class="holotok__figure">
      <div class="holotok__bloom"></div>
      <div class="holotok__art"></div>
      <div class="holotok__rim"></div>
      <div class="holotok__sweep"></div>
    </div>

    <img
      v-if="plate === 'sprite'"
      class="holotok__base"
      :src="pedestalUrl"
      alt=""
      draggable="false"
      loading="lazy"
      decoding="async"
    />
    <div v-if="plate === 'sprite'" class="holotok__baseTint"></div>
    <!-- The status ring is measured against the pedestal ARTWORK, so it has nowhere
         to be once that artwork is gone: it ends up round the figure's waist. With a
         prop under the figure the prop's own trim carries the status instead. -->
    <div v-if="plate === 'sprite'" class="holotok__ring"></div>
    <!-- A second, independent ring for the side, on the plate's own ground ellipse
         rather than the containment groove the status ring uses — the two must
         never merge into one ring carrying two facts. See the `sideColor` prop. -->
    <div v-if="showSideRing" class="holotok__sideRing" :data-side="'true'"></div>
    <!-- The side's sign, standing upright at the front of that ring. Upright
         and not laid on the ground plane with it: the ring is a thing on the
         floor, the mark is a label, and a foreshortened emblem is unreadable
         at the size a board draws a token. -->
    <svg v-if="showSideMark" class="holotok__sideMark" viewBox="0 0 24 24" aria-hidden="true">
      <path :d="sideEmblem"/>
    </svg>
    <div class="holotok__core"></div>

    <FlowSignMark v-if="signed" class="holotok__flowSign" :size="flowSignSize"/>

    <slot></slot>
  </div>
</template>

<style scoped>
/* ------------------------------------------------------------------
   HoloToken — a character projected above a pedestal.

   Two rules govern the whole file.

   Geometry: nothing is placed by eye. The pedestal artwork has a lens and
   a containment ring at known coordinates, measured off the asset; every
   other element — beam apex, power dot, ring, the projection's vanishing
   point — is derived from those. Change --ht-size and the alignment holds
   to the pixel, because it is the same arithmetic at every scale.
   The stage fractions are mirrored in holo/geometry.ts, which is what the
   tactical map uses to seat the pedestal on a grid cell.

   Cost: one mask raster and one background raster per token, no filters,
   and only transform/opacity animated.
   ------------------------------------------------------------------ */

.holotok {
  --ht-size: 180px;
  --ht-color: #22e3ff;
  --ht-color-soft: color-mix(in oklab, var(--ht-color) 52%, #ffffff);
  /*
   * The top of the ramp, and the reason it is no longer nearly black.
   *
   * The ramp runs bright at the emitter and deep at the head, which is the right
   * direction for a light thrown from below — but at 58% colour against #06121c it
   * put the darkest tint the component owns exactly where the face is, then the art's
   * own luminance multiplied into it. A head is the one region a player reads a token
   * by, and it was arriving at the bottom of two curves at once. Mixing far less of the
   * cold means the ramp still falls off upward without crushing the thing it falls off
   * across; the vertical reading survives because --ht-color-soft still lifts the base.
   */
  --ht-color-deep: color-mix(in oklab, var(--ht-color) 82%, #0a1622);

  /* Shaping, as fractions of the figure box. The board tier rewrites both — see
     BOARD_MAX_PX in the script for why they are variables at all. */
  --ht-dissolve: 38%; /* how far up the figure melts into the beam */
  --ht-vignette: 66%; /* where the elliptical falloff starts eating the silhouette */
  --ht-rim-strength: 0.5;

  /* ---- the card ----
     Half the width of the panel's bottom edge, as a fraction of the token's width.
     The top edge is always the full width: a panel that widens as it rises is the
     shape a projector actually throws, and it reads as thrown light from any angle,
     which a rectangle floating in the air never does.

     Nearly a point, because the emitter is one. With a cone drawn under it the base
     could afford to be a hand's width — the cone carried the eye from the lens up to
     it and the join was inside the light. That cone is gone, so this edge IS the
     join: anything wider than the lens reads as a panel starting in mid-air above a
     projector that has nothing to do with it. */
  --ht-card-half-bot: 0.05;
  --ht-card-opacity: 0.88;
  /* Tint, not recolour. The artwork keeps its own hue and reads as itself; the colour
     only has to carry whose side this figure is on. Pushed further the board becomes
     two masses of red and gold in which no character is recognisable — which is the
     readability the token exists to provide, spent on decoration. */
  --ht-card-tint: 0.2;
  /* How far in from the token's sides the panel dissolves.
     The panel only reaches its own edges near the TOP — lower down the trapezoid has
     already drawn itself in past this margin — so one even fade produces an edge that
     is crisp where the shape is doing the reading and soft where it is just a cut
     across the widest part of the picture. Which is the right way round: the raked
     sides are what say "projected", and the top is what says "photograph". */
  --ht-card-feather: 13%;

  --ht-intensity: 1;
  --ht-figure-opacity: 0.86;
  --ht-float: 3%;
  --ht-dur: 6s;
  --ht-phase: 0s;
  /*
   * The long cycle: how often a projection does anything other than hold still.
   *
   * Only the interference sweep is paced by this now, and only at quality='high' —
   * which the tactical map assigns by on-screen size, so it is in play exactly when
   * the viewer is zoomed in far enough that a handful of figures fill the screen.
   * That gating is what makes it survivable at all: a per-token event happens at
   * N times its own rate from where the viewer is sitting, so an effect that is
   * gentle on three figures is churn on thirty. See ht-glitch for the rest of it.
   */
  --ht-idle: 54s;
  --ht-idle-phase: 0s;
  --ht-ground-tilt: 0deg;

  /* ---- measured off the pedestal artwork ---- */
  --ht-base-ar: 1.4382;  /* 512 / 356 */
  --ht-emit-fx: 0.495;   /* lens, fraction across the pedestal image */
  --ht-emit-fy: 0.085;   /* lens, fraction down the pedestal image */
  --ht-ring-fx: 0.49;    /* containment ring centre */
  --ht-ring-fy: 0.335;
  --ht-ring-fw: 0.46;    /* ring diameter, fraction of pedestal width */
  --ht-ring-tilt: 68deg; /* the tilt that reproduces its measured ellipse */

  /* The plate's contact ellipse, measured off the artwork's alpha: widest scanline
     at y=227/356, spanning x=5..508 of 512. Its foreshortening is cos(--ht-ring-tilt),
     the same camera the containment ring was measured at. Status rings are laid on
     THIS ellipse rather than on the host grid's cell, because the two are rendered
     from different camera heights and a ring split between them always drifts. */
  --ht-ground-cx: 0.501;
  --ht-ground-cy: 0.6376;
  --ht-ground-rx: 0.4912;
  --ht-ground-squash: 0.3746; /* cos(68deg) */

  /* ---- stage layout (unitless so the vars can divide each other) ---- */
  --ht-token-ar: 1.55;   /* token height / width */
  --ht-base-w: 0.84;     /* pedestal width, fraction of token width */
  --ht-base-b: 0.055;    /* pedestal bottom, fraction of token height */
  --ht-base-h: calc(var(--ht-base-w) / var(--ht-base-ar) / var(--ht-token-ar));

  --ht-emit-x: calc(0.5 + (var(--ht-emit-fx) - 0.5) * var(--ht-base-w));
  --ht-emit-y: calc(var(--ht-base-b) + (1 - var(--ht-emit-fy)) * var(--ht-base-h));

  --ht-figure-b: 0.4;    /* the projection stands on the lens ... */
  --ht-figure-h: 0.59;   /* ... and reaches nearly the top of the token */

  /* Apex of the light cone, expressed inside the figure box. Both the beam
     and the projection's mask use it, which is what makes the body appear to
     be *made of* the beam rather than to be standing in front of it. */
  --ht-cone-y: calc(
    (var(--ht-figure-b) + var(--ht-figure-h) - var(--ht-emit-y)) / var(--ht-figure-h)
  );
  --ht-cone-angle: 24deg; /* half-angle */
  --ht-cone-soft: 10deg;  /* angular blur along the cone wall */

  position: relative;
  display: block;
  width: var(--ht-size);
  aspect-ratio: 1 / var(--ht-token-ar);
  border: 0;
  padding: 0;
  background: none;
  color: inherit;
  font: inherit;
  isolation: isolate;
  /*
   * Layout and style, but NOT paint.
   *
   * Paint containment turns the token box into a clip rectangle, and the token is now
   * drawn on its own compositing layer at the board's zoom and scaled back down. A
   * fractional scale samples that layer's edge texel against the transparent nothing
   * outside it, and the clip guarantees there is an abrupt edge there to sample: the
   * result was a one-pixel bright hairline up the left side of every figure — faint
   * alone, but one per token across the whole board.
   *
   * Nothing is lost by dropping it. Containment's clip was never doing any shaping
   * work here: the beam fades out before the side walls, the projection is bounded by
   * its own matte, and the bloom by its radial. Layout and style containment, which
   * are what keep forty tokens from invalidating each other, are unaffected.
   */
  contain: layout style;
}

/* Off screen or explicitly parked: stop animating. */
.holotok[data-active='false'],
.holotok[data-active='false'] * {
  animation-play-state: paused !important;
}

/* ---------- the projection ---------- */

/* The wrapper carries the silhouette AND the cone, so the artwork, the bloom
   and the interference band are all carved by one mask raster. */
.holotok__figure {
  position: absolute;
  left: 0;
  bottom: calc(var(--ht-figure-b) * 100%);
  width: 100%;
  height: calc(var(--ht-figure-h) * 100%);
  pointer-events: none;

  -webkit-mask-image:
    var(--ht-art),
    conic-gradient(
      from 180deg at calc(var(--ht-emit-x) * 100%) calc(var(--ht-cone-y) * 100%),
      transparent 0deg,
      transparent calc(180deg - var(--ht-cone-angle) - var(--ht-cone-soft)),
      #000 calc(180deg - var(--ht-cone-angle)),
      #000 calc(180deg + var(--ht-cone-angle)),
      transparent calc(180deg + var(--ht-cone-angle) + var(--ht-cone-soft)),
      transparent 360deg
    ),
    linear-gradient(
      to top,
      transparent 0%,
      rgba(0, 0, 0, 0.22) calc(var(--ht-dissolve) / 6),
      #000 var(--ht-dissolve)
    ),
    radial-gradient(84% 86% at 50% 42%, #000 var(--ht-vignette), transparent 100%);
  mask-image:
    var(--ht-art),
    conic-gradient(
      from 180deg at calc(var(--ht-emit-x) * 100%) calc(var(--ht-cone-y) * 100%),
      transparent 0deg,
      transparent calc(180deg - var(--ht-cone-angle) - var(--ht-cone-soft)),
      #000 calc(180deg - var(--ht-cone-angle)),
      #000 calc(180deg + var(--ht-cone-angle)),
      transparent calc(180deg + var(--ht-cone-angle) + var(--ht-cone-soft)),
      transparent 360deg
    ),
    linear-gradient(
      to top,
      transparent 0%,
      rgba(0, 0, 0, 0.22) calc(var(--ht-dissolve) / 6),
      #000 var(--ht-dissolve)
    ),
    radial-gradient(84% 86% at 50% 42%, #000 var(--ht-vignette), transparent 100%);
  mask-mode: var(--ht-art-mode), alpha, alpha, alpha;
  -webkit-mask-size: contain, cover, cover, cover;
  mask-size: contain, cover, cover, cover;
  -webkit-mask-position: center bottom, center, center, center;
  mask-position: center bottom, center, center, center;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-composite: source-in, source-in, source-in;
  mask-composite: intersect, intersect, intersect;

  animation: ht-float var(--ht-dur) ease-in-out var(--ht-phase) infinite;
  will-change: transform;
  transition: opacity 260ms ease;
}

/* Colour only, no shape: scanlines (overlay) over the artwork (luminosity)
   over the tint ramp. The art keeps its shading but surrenders its hue to
   --ht-color, and all three blend inside the element. */
.holotok__art {
  position: absolute;
  inset: 0;
  background-image:
    /*
     * Scanlines, ruled in the TOKEN's units rather than in CSS pixels.
     *
     * A fixed 1-on-2-off ruling is a 3px period, which on a 50px figure is right at
     * the sampling limit: it shimmers, moirés against the zoom, and adds itself to
     * the noise it was meant to replace. Tied to --ht-size it stays the same number
     * of lines across the projection at every size, which is what a raster actually
     * does, and it lands well clear of a pixel. Softened to a gradient rather than a
     * hard edge for the same reason.
     */
    repeating-linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.1) 0,
      transparent calc(var(--ht-size) * 0.014),
      transparent calc(var(--ht-size) * 0.028)
    ),
    var(--ht-art),
    linear-gradient(
      to top,
      var(--ht-color-soft) 0%,
      var(--ht-color) 28%,
      var(--ht-color-deep) 100%
    );
  background-blend-mode: overlay, luminosity, normal;
  background-repeat: repeat, no-repeat, no-repeat;
  background-position: center, center bottom, center;
  background-size: auto, contain, cover;
  opacity: calc(var(--ht-figure-opacity) * var(--ht-intensity));
}

/* Light pooling inside the projection, clipped to the silhouette by the
   wrapper. quality="high" only. */
.holotok__bloom {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, var(--ht-color-soft), var(--ht-color));
  -webkit-mask-image: radial-gradient(54% 58% at 50% 44%, #000 0%, transparent 84%);
  mask-image: radial-gradient(54% 58% at 50% 44%, #000 0%, transparent 84%);
  opacity: calc(0.3 * var(--ht-intensity));
  mix-blend-mode: plus-lighter;
}

/*
 * The contour.
 *
 * Painted from the rim bitmap the extractor derives from the same alpha the figure
 * is cut by, so it lands on the silhouette by construction rather than by a blur
 * that happens to look close. Near-white at the top and the projection's own colour
 * at the base: an edge that is pure hue reads as an outline drawn round a sticker,
 * while one that runs to white reads as light gathering on a surface turning away.
 *
 * plus-lighter, because it is light being ADDED to the body underneath — a normally
 * composited edge would replace the art it sits on and punch a bright ring through
 * the shoulders instead of lighting them.
 */
.holotok__rim {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    color-mix(in oklab, var(--ht-color-soft) 70%, transparent) 0%,
    var(--ht-color-soft) 34%,
    #ffffff 100%
  );
  -webkit-mask-image: var(--ht-rim);
  mask-image: var(--ht-rim);
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-position: center bottom;
  mask-position: center bottom;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  opacity: calc(var(--ht-rim-strength) * var(--ht-intensity));
  mix-blend-mode: plus-lighter;
  pointer-events: none;
}

/* No separable outline (raw art): there is nothing honest to draw a contour from. */
.holotok[data-rim='false'] .holotok__rim {
  display: none;
}

/* Interference band travelling up the projection. */
.holotok__sweep {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 14%;
  background: linear-gradient(
    to bottom,
    transparent,
    color-mix(in oklab, var(--ht-color-soft) 52%, transparent) 45%,
    transparent
  );
  opacity: calc(0.5 * var(--ht-intensity));
  mix-blend-mode: plus-lighter;
  /* Rare, not constant. A band climbing every figure on the board every few seconds
     is thirty things moving at once and nothing to look at; the eye reads it as
     churn. On a long cycle it becomes an event again — you notice one projection
     resettle and then it is still. --ht-phase spreads the board so no two do it
     together. */
  animation: ht-sweep var(--ht-idle) linear var(--ht-idle-phase) infinite;
  will-change: transform;
}

/*
 * There is no particle field.
 *
 * There was: a tiled dot field of 1px radial gradients rising out of the lens. On a
 * card it reads as motes; on the board a token is 35-60px wide, so the field was a
 * 12px box carrying a 6px tile of sub-pixel dots, crawling upward, on every figure
 * at once. That is not motes, it is video snow, and it is snow by construction —
 * there is no size or opacity at which a 1px dot at that density becomes anything
 * else. Scale is what makes an effect an effect, and this one had none left.
 *
 */

/* ---------- the card ----------
   A panel thrown up out of the emitter. Nothing here inspects the artwork: the
   picture is placed, tinted and made translucent, and the shape does the work that
   the matte used to be asked for. */

.holotok[data-variant='card'] {
  /* The panel stands clear of the plate so the beam under it is a visible length of
     light rather than a seam. The figure variant packs these tighter because its
     projection dissolves into the beam and the join has to be inside the body. */
  --ht-figure-b: 0.4;
  --ht-figure-h: 0.56;
}

.holotok[data-variant='card'][data-scale='board'] {
  --ht-figure-b: 0.37;
  --ht-figure-h: 0.61;
  --ht-card-opacity: 0.92;
}

.holotok__card {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(var(--ht-figure-b) * 100%);
  height: calc(var(--ht-figure-h) * 100%);
  /*
   * Full width at the top, narrow at the base — and the base is centred on the
   * EMITTER, not on the token box.
   *
   * The two are not the same point: the lens sits at --ht-emit-x, which the pedestal
   * artwork puts slightly off centre, and the plate is additionally rotated to lie
   * along the floor. Striking the panel from the middle of the box instead left it
   * a couple of pixels off the light it is supposed to be made of, so the base of the
   * trapezoid and the bright pool on the ground disagreed about where the projector
   * was.
   */
  clip-path: polygon(
    0% 0%,
    100% 0%,
    calc(var(--ht-emit-x) * 100% + var(--ht-card-half-bot) * 100%) 100%,
    calc(var(--ht-emit-x) * 100% - var(--ht-card-half-bot) * 100%) 100%
  );
  /*
   * Both horizontal edges are given up, for different reasons and by different
   * amounts. The bottom dissolves, so the panel emerges out of the lens rather than
   * resting on it with a cut edge — and only just, over a sliver of the height. Any
   * longer and the apex is faded away before it arrives, which leaves the projection
   * hanging clear of the emitter it is coming out of. The top only softens: a hard
   * horizontal rule across the top is the one line in the whole token that could not
   * be light, and it is what makes a projection read as a photograph pasted into the
   * scene. It cannot fade far, because the crop puts the face directly beneath it and
   * a face is the entire identifying content of a portrait at this size.
   */
  -webkit-mask-image:
    linear-gradient(to top, transparent 0%, #000 6%, #000 72%, rgba(0, 0, 0, 0.28) 100%),
    linear-gradient(
      to right,
      transparent 0%,
      #000 var(--ht-card-feather),
      #000 calc(100% - var(--ht-card-feather)),
      transparent 100%
    );
  mask-image:
    linear-gradient(to top, transparent 0%, #000 6%, #000 72%, rgba(0, 0, 0, 0.28) 100%),
    linear-gradient(
      to right,
      transparent 0%,
      #000 var(--ht-card-feather),
      #000 calc(100% - var(--ht-card-feather)),
      transparent 100%
    );
  -webkit-mask-composite: source-in;
  mask-composite: intersect;
  opacity: calc(var(--ht-card-opacity) * var(--ht-intensity));
  overflow: hidden;
  pointer-events: none;
  animation: ht-float var(--ht-dur) ease-in-out var(--ht-phase) infinite;
  will-change: transform;
  transition: opacity 260ms ease;
}

.holotok__cardArt {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* Character art is framed head-and-torso with headroom above. Cropping from the
     top third keeps the face — the only part of a portrait that identifies anyone at
     token size — and spends the loss on the legs, which never did. */
  object-position: center 22%;
  user-select: none;
  -webkit-user-drag: none;
}

/* No portrait to project: the panel still stands, carrying the character's
   initials instead of a picture. Same panel, same beam, same tint — the only
   thing missing is the photograph. */
.holotok__cardFallback {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    to top,
    var(--ht-color-deep) 0%,
    var(--ht-color) 60%,
    var(--ht-color-soft) 100%
  );
  color: #ffffff;
  font-family: inherit;
  font-weight: 700;
  font-size: calc(var(--ht-size) * 0.16);
  letter-spacing: 0.06em;
  text-shadow: 0 0 calc(var(--ht-size) * 0.03) rgba(0, 0, 0, 0.55);
  user-select: none;
}

/* Hue only. `color` blending takes the tint's hue and saturation and keeps the
   artwork's own luminance, so the picture's modelling, its light and its detail all
   survive being coloured — which is exactly what a wash of paint over the top, or a
   luminosity blend under it, destroys. */
.holotok__cardTint {
  position: absolute;
  inset: 0;
  background: var(--ht-color);
  mix-blend-mode: color;
  opacity: var(--ht-card-tint);
}

.holotok__cardLines {
  position: absolute;
  inset: 0;
  background:
    /* Ruled in the token's own units, so the panel carries the same number of lines
       at every zoom instead of moiring against it. */
    repeating-linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.11) 0,
      transparent calc(var(--ht-size) * 0.014),
      transparent calc(var(--ht-size) * 0.028)
    ),
    /* The beam lighting the underside of its own panel. */
    linear-gradient(
      to top,
      color-mix(in oklab, var(--ht-color-soft) 62%, transparent) 0%,
      transparent 46%
    );
  mix-blend-mode: plus-lighter;
  opacity: calc(0.9 * var(--ht-intensity));
}

/* The edge. A clip-path leaves no border to style, but a drop-shadow is taken from
   the clipped alpha, so it traces the trapezoid exactly — including the two raked
   sides, which is where the shape has to read. */
.holotok[data-variant='card'] .holotok__card {
  filter: drop-shadow(0 0 calc(var(--ht-size) * 0.022) var(--ht-color));
}

.holotok[data-variant='card'][data-quality='low'] .holotok__card {
  filter: none;
  animation: none;
}

/* ---------- pedestal ---------- */

.holotok__base,
.holotok__baseTint {
  position: absolute;
  left: 50%;
  bottom: calc(var(--ht-base-b) * 100%);
  width: calc(var(--ht-base-w) * 100%);
  aspect-ratio: var(--ht-base-ar);
  translate: -50% 0;
  pointer-events: none;
  /* Lie along the host surface's x axis. Rotating about the lens keeps the beam
     apex fixed, so the projection still rises out of the emitter. */
  transform: rotate(var(--ht-ground-tilt));
  transform-origin: calc(var(--ht-emit-fx) * 100%) calc(var(--ht-emit-fy) * 100%);
}

.holotok__base {
  display: block;
  user-select: none;
  -webkit-user-drag: none;
}

/* The backlight re-emitted by the pedestal, clipped to the pedestal artwork
   and concentrated around the lens, so recolouring reads as *its* emissives
   heating up rather than as a wash of colour laid over the metal. */
.holotok__baseTint {
  background: radial-gradient(
    62% 58% at calc(var(--ht-emit-fx) * 100%) calc(var(--ht-emit-fy) * 100%),
    var(--ht-color-soft) 0%,
    color-mix(in oklab, var(--ht-color) 70%, transparent) 34%,
    color-mix(in oklab, var(--ht-color) 22%, transparent) 68%,
    transparent 100%
  );
  -webkit-mask-image: var(--ht-pedestal);
  mask-image: var(--ht-pedestal);
  -webkit-mask-size: 100% 100%;
  mask-size: 100% 100%;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  opacity: calc(0.75 * var(--ht-intensity));
  mix-blend-mode: plus-lighter;
}

/* Power dot: small, and sitting exactly on the lens. */
.holotok__core {
  position: absolute;
  left: calc(var(--ht-emit-x) * 100%);
  bottom: calc(var(--ht-emit-y) * 100%);
  width: calc(var(--ht-base-w) * 13%);
  aspect-ratio: 1.35;
  translate: -50% 50%;
  border-radius: 50%;
  background: radial-gradient(
    closest-side,
    #ffffff 0%,
    var(--ht-color-soft) 26%,
    color-mix(in oklab, var(--ht-color) 40%, transparent) 56%,
    transparent 100%
  );
  opacity: calc(0.95 * var(--ht-intensity));
  mix-blend-mode: plus-lighter;
  pointer-events: none;
  animation: ht-core calc(var(--ht-dur) * 0.5) ease-in-out var(--ht-phase) infinite;
}

/* Orbiting ring, laid into the groove the artwork already has. */
.holotok__ring {
  position: absolute;
  left: calc((0.5 + (var(--ht-ring-fx) - 0.5) * var(--ht-base-w)) * 100%);
  bottom: calc((var(--ht-base-b) + (1 - var(--ht-ring-fy)) * var(--ht-base-h)) * 100%);
  width: calc(var(--ht-ring-fw) * var(--ht-base-w) * 100%);
  aspect-ratio: 1;
  translate: -50% 50%;
  border-radius: 50%;
  background: conic-gradient(
    from 0deg,
    transparent 0deg,
    var(--ht-color) 66deg,
    #ffffff 94deg,
    var(--ht-color) 122deg,
    transparent 198deg,
    transparent 360deg
  );
  -webkit-mask-image: radial-gradient(closest-side, transparent 78%, #000 84%, #000 95%, transparent 100%);
  mask-image: radial-gradient(closest-side, transparent 78%, #000 84%, #000 95%, transparent 100%);
  transform: rotateX(var(--ht-ring-tilt));
  opacity: calc(0.8 * var(--ht-intensity));
  mix-blend-mode: plus-lighter;
  pointer-events: none;
  animation: ht-ring calc(var(--ht-dur) * 1.3) linear var(--ht-phase) infinite;
  will-change: transform;
}

/* A second, independent ring for the side — see the `sideColor` prop and the
   `showSideRing` gate. Positioned on the plate's own ground ellipse (the same
   numbers the contact shadow in `PlayerTacticalMap.vue` is measured from), not
   on the containment groove `.holotok__ring` uses, so the two rings can never
   merge into one that would say two things about the token at once. */
.holotok__sideRing {
  position: absolute;
  left: calc((0.5 + (var(--ht-ground-cx) - 0.5) * var(--ht-base-w)) * 100%);
  bottom: calc((var(--ht-base-b) + (1 - var(--ht-ground-cy)) * var(--ht-base-h)) * 100%);
  width: calc(var(--ht-ground-rx) * var(--ht-base-w) * 2 * 100%);
  aspect-ratio: 1;
  translate: -50% 50%;
  border-radius: 50%;
  border: calc(var(--ht-size) * 0.014) solid var(--ht-side-color, transparent);
  transform: rotate(var(--ht-ground-tilt)) scaleY(var(--ht-ground-squash));
  opacity: 0.85;
  box-shadow: 0 0 calc(var(--ht-size) * 0.018) var(--ht-side-color, transparent);
  pointer-events: none;
}

/* The emblem sits at the FRONT of the side ring — the ring's centre carried
   down by its own foreshortened radius, converted from a width fraction into a
   height one through the token's aspect ratio, which is why every var here can
   divide every other one. Stroked in the side's colour, with the ring's glow,
   so the two read as one mark. */
.holotok__flowSign {
  z-index: 7;
}

.holotok__sideMark {
  position: absolute;
  left: calc((0.5 + (var(--ht-ground-cx) - 0.5) * var(--ht-base-w)) * 100%);
  bottom: calc((
      var(--ht-base-b) + (1 - var(--ht-ground-cy)) * var(--ht-base-h)
      - var(--ht-ground-rx) * var(--ht-base-w) * var(--ht-ground-squash) / var(--ht-token-ar)
  ) * 100%);
  width: calc(var(--ht-size) * 0.15);
  height: calc(var(--ht-size) * 0.15);
  translate: -50% 50%;
  fill: none;
  stroke: var(--ht-side-color, transparent);
  stroke-width: 2.1;
  stroke-linecap: round;
  stroke-linejoin: round;
  filter: drop-shadow(0 0 calc(var(--ht-size) * 0.02) var(--ht-side-color, transparent));
  opacity: 0.95;
  pointer-events: none;
}

/* ---------- scale budget ----------
   See BOARD_MAX_PX. On a board the character takes the space back: the dissolve
   stops at the knees instead of the waist, the elliptical falloff retreats to the
   corners it was meant to soften rather than rounding the whole body off, the cone
   opens enough to clear a pair of shoulders, and the figure box grows into the air
   the atmosphere was using. The contour carries proportionally more, because it is
   the part that still resolves. */

.holotok[data-scale='board'] {
  --ht-dissolve: 15%;
  --ht-vignette: 88%;
  --ht-rim-strength: 0.6;
  --ht-cone-angle: 31deg;
  --ht-cone-soft: 7deg;
  --ht-figure-b: 0.35;
  --ht-figure-h: 0.68;
  --ht-figure-opacity: 0.94;
}

/* The interior fill is what stops resolving first — a 54%/58% radial bloom inside a
   sixty-pixel body is a flat wash over the whole of it, which is contrast removed
   from the one thing that needed it. */
.holotok[data-scale='board'] .holotok__bloom {
  opacity: calc(0.16 * var(--ht-intensity));
}

/* ---------- states ---------- */

/* With no ring under the token, the states are carried entirely by the projection
   itself gaining power - which is the hologram's own idiom rather than a UI badge. */
.holotok[data-marker='hover'],
.holotok[data-marker='selected'],
.holotok[data-marker='targeted'] {
  --ht-figure-opacity: 1;
  --ht-card-opacity: 1;
}

/* Under an armed ability the projection is already re-lit in that ability's colour
   by the caller; what marks it as *live* is that the whole emitter surges, on a
   cycle short enough to read as an alarm rather than as the idle float. */
.holotok[data-marker='targeted'] {
  animation: ht-targeted 1.05s ease-in-out infinite;
}

.holotok[data-marker='targeted'] .holotok__ring {
  animation-duration: calc(var(--ht-dur) * 0.42);
  opacity: calc(1 * var(--ht-intensity));
}

@keyframes ht-targeted {
  0%,
  100% {
    filter: none;
  }
  50% {
    filter: brightness(1.28) saturate(1.2);
  }
}

/* Nothing to project until the matte is out. */
.holotok[data-ready='false'] .holotok__figure {
  opacity: 0;
}

.holotok[data-state='dimmed'] {
  --ht-intensity: 0.42;
  --ht-figure-opacity: 0.5;
  /* The movement ghost. It has to read as the same character previewed, not as a
     second one on the board, so it keeps the shape and gives up the substance. */
  --ht-card-opacity: 0.4;
}

/* Signal loss. Applied to the art, not the floating wrapper, so the two
   transforms never fight over the same property. */
.holotok[data-state='unstable'] .holotok__art,
.holotok[data-state='unstable'] .holotok__cardArt {
  animation: ht-glitch 3.1s steps(1, end) var(--ht-phase) infinite;
}

.holotok[data-state='offline'] {
  --ht-intensity: 0.25;
}

.holotok[data-state='offline'] .holotok__figure,
.holotok[data-state='offline'] .holotok__card,
.holotok[data-state='offline'] .holotok__ring,
.holotok[data-state='offline'] .holotok__sideRing,
.holotok[data-state='offline'] .holotok__sideMark,
.holotok[data-state='offline'] .holotok__sweep {
  display: none;
}

/* ---------- quality tiers ----------
   low    figure, pedestal, floor glow — no animation
   medium + float, core pulse, ring
   high   + sweep, bloom                                          */

.holotok[data-quality='low'] .holotok__ring,
.holotok[data-quality='low'] .holotok__sweep,
.holotok[data-quality='low'] .holotok__bloom,
.holotok[data-quality='medium'] .holotok__sweep,
.holotok[data-quality='medium'] .holotok__bloom {
  display: none;
}

.holotok[data-quality='low'] .holotok__figure,
.holotok[data-quality='low'] .holotok__core {
  animation: none;
}

/* One fewer background raster per token. */
.holotok[data-quality='low'] .holotok__art {
  background-image: var(--ht-art), linear-gradient(to top, var(--ht-color-soft), var(--ht-color-deep));
  background-blend-mode: luminosity, normal;
  background-position: center bottom, center;
  background-size: contain, cover;
}

/* ---------- motion ---------- */

@keyframes ht-float {
  0%,
  100% {
    transform: translate3d(0, 0, 0);
  }
  50% {
    transform: translate3d(0, calc(var(--ht-float) * -1), 0);
  }
}

@keyframes ht-core {
  0%,
  100% {
    transform: scale(1);
    opacity: calc(0.82 * var(--ht-intensity));
  }
  50% {
    transform: scale(1.16);
    opacity: calc(1 * var(--ht-intensity));
  }
}

@keyframes ht-ring {
  from {
    transform: rotateX(var(--ht-ring-tilt)) rotate(0deg);
  }
  to {
    transform: rotateX(var(--ht-ring-tilt)) rotate(360deg);
  }
}

/*
 * One pass up the projection, then nothing for the rest of the cycle.
 *
 * The travel is packed into the first eighth and the band is invisible either side
 * of it, so the long tail is genuine stillness rather than a slow crawl. Fading at
 * both ends because a band that appears or restarts at full strength reads as a
 * flicker, which is the opposite of what it is for.
 */
@keyframes ht-sweep {
  0% {
    transform: translate3d(0, 640%, 0);
    opacity: 0;
  }
  1.5%,
  9% {
    opacity: calc(0.42 * var(--ht-intensity));
  }
  11%,
  100% {
    transform: translate3d(0, -120%, 0);
    opacity: 0;
  }
}

/*
 * There is no idle glitch, and there should not be one.
 *
 * A projection that stutters on its own was tried and removed. Two things are wrong
 * with it, and the first is not fixable by tuning: a per-token event multiplies by
 * the number of tokens. Thirty figures each stuttering once a minute is something
 * blinking somewhere on the board every two seconds — the rate a viewer actually
 * experiences is the board's, never the token's, so "rare" per figure is still
 * constant churn in front of them. Any per-token idle animation has this shape.
 *
 * The second is the technique. Dropping global opacity for a few frames does not
 * read as a hologram; it reads as the interface failing to draw, because that is
 * what a briefly-transparent element looks like in every other context a viewer has
 * ever seen. Holographic instability is spatial — a scanline tearing, a band
 * displacing — not an element blinking.
 *
 * So instability is not decoration here. It is a STATE, driven by data: `unstable`
 * says something is wrong with this character, and glitching then means something.
 * A calm board stays perfectly calm, which is also what lets a figure that is not
 * calm be noticed at all.
 */

/*
 * The `unstable` state — a signal that is genuinely failing.
 *
 * No sideways displacement, whatever it would do for the effect: a figure that jumps
 * horizontally leaves the cell it is standing on, and where a body is standing is
 * not something the art is allowed to be playful about on a tactical map. The break
 * is carried vertically instead, as a collapse and recovery of the projection's own
 * height, which is the axis nothing is measured along.
 */
@keyframes ht-glitch {
  0%,
  86%,
  100% {
    opacity: var(--ht-figure-opacity);
    transform: scaleY(1);
  }
  88% {
    opacity: 0.3;
    transform: scaleY(0.94);
  }
  90% {
    opacity: var(--ht-figure-opacity);
    transform: scaleY(1.03);
  }
  92% {
    opacity: 0.45;
    transform: scaleY(0.98);
  }
}

@media (prefers-reduced-motion: reduce) {
  .holotok,
  .holotok * {
    animation: none !important;
  }

  /* The surge is gone, so the marker has to hold its peak instead of pulsing to it. */
  .holotok[data-marker='targeted'] {
    filter: brightness(1.28) saturate(1.2);
  }
}
</style>
